import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../../components/AppIcon';

const TermsOfServicePage = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Intime ATS | Service Agreement</title>
        <meta name="description" content="Read Intime's Terms of Service to understand the terms and conditions for using our applicant tracking system and related services." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://intime.com/legal/terms" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="border-b border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-white" />
                </div>
                <span className="text-2xl font-bold text-gray-900">Intime</span>
              </Link>
              
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Home
                </Link>
                <Link to="/pricing" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Pricing
                </Link>
                <Link to="/careers" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Careers
                </Link>
                <Link to="/login" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Sign In
                </Link>
              </nav>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-600">
              Last updated: December 17, 2024
            </p>
          </div>

          <div className="prose prose-lg prose-gray max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <div className="flex items-start">
                <Icon name="Scale" size={20} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Agreement Overview
                  </h3>
                  <p className="text-blue-800 text-base">
                    By using Intime's applicant tracking system, you agree to these Terms of Service. 
                    Please read them carefully as they govern your use of our platform and services.
                  </p>
                </div>
              </div>
            </div>

            {/* Placeholder content sections */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                1. Acceptance of Terms
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="CheckCircle" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will outline the acceptance and binding nature of these terms.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                2. Service Description
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Monitor" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will describe the services provided by Intime's ATS platform.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                3. User Accounts and Registration
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="UserPlus" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will detail account creation requirements and responsibilities.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                4. Acceptable Use Policy
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="AlertTriangle" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will outline prohibited uses and user conduct requirements.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                5. Payment Terms and Billing
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="CreditCard" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will detail payment terms, billing cycles, and refund policies.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                6. Intellectual Property Rights
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Copyright" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will address ownership of content and intellectual property.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                7. Data Protection and Privacy
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Lock" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will reference our privacy policy and data handling practices.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                8. Service Availability and Support
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Headphones" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will outline service level commitments and support offerings.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                9. Limitation of Liability
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="ShieldAlert" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will define limitations on our liability for service use.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                10. Termination
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="XCircle" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will outline conditions for account termination and data retention.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                11. Governing Law and Disputes
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Gavel" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will specify governing law and dispute resolution procedures.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                12. Changes to Terms
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Edit" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will explain how we handle updates to these terms of service.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                Contact Information
              </h2>
              <p className="text-blue-800 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2 text-blue-800">
                <p><strong>Email:</strong> legal@intime.com</p>
                <p><strong>Address:</strong> [Company Address]</p>
                <p><strong>Phone:</strong> [Company Phone]</p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <Link to="/" className="flex items-center space-x-2 mb-4 md:mb-0">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-white" />
                </div>
                <span className="text-2xl font-bold">Intime</span>
              </Link>
              
              <div className="flex items-center space-x-6 text-sm text-gray-400">
                <Link to="/legal/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/legal/terms" className="text-white">
                  Terms of Service
                </Link>
                <span>&copy; 2024 Intime. All rights reserved.</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsOfServicePage;